import { TableBody, TableCell, TableRow, Typography } from '@mui/material';
import { DragHandle, Delete } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTranslation } from 'react-i18next';
import StyledTableRow from '../../../../../app/components/StyledTableRow';
import { formatCurrency } from '../../../../../app/utils/formatters';
import AddTextItemCell from '../../../../../app/components/AddTextItemCell';
import { SelectOption } from '../../../../../app/components/SelectOption';
import MealItem from '../../../../../app/data/models/mealItem';

interface Props {
  selectedMealItems: MealItem[];
  mealItemSelectionList: SelectOption[];
  target: string;
  onRemoveMealItem: (id: string) => void;
  onAddItem: (id: string) => void;
}

const SortableMenuItem: React.FC<{
  mealItem: MealItem;
  target: string;
  onRemoveMealItem: (id: string) => void;
}> = ({ mealItem, target, onRemoveMealItem }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: mealItem.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <StyledTableRow ref={setNodeRef} style={style} {...attributes}>
      <TableCell {...listeners}>
        <DragHandle />
      </TableCell>
      <TableCell component="th" scope="row">
        {mealItem.name}
      </TableCell>
      <TableCell align="right">{formatCurrency(mealItem.price)}</TableCell>
      <TableCell align="right">
        <LoadingButton
          loading={target === mealItem.id}
          startIcon={<Delete />}
          color="error"
          sx={{ width: '36px', minWidth: '36px' }}
          onClick={() => onRemoveMealItem(mealItem.id)}
        />
      </TableCell>
    </StyledTableRow>
  );
};

export const MenuItemList: React.FC<Props> = ({
  selectedMealItems,
  mealItemSelectionList,
  target,
  onRemoveMealItem,
  onAddItem,
}) => {
  const { t } = useTranslation();

  return (
    <TableBody>
      {selectedMealItems.length === 0 ? (
        <TableRow>
          <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
            <Typography color="text.secondary">
              {t('menu.details.no-meal-items')}
            </Typography>
          </TableCell>
        </TableRow>
      ) : (
        selectedMealItems.map((mealItem) => (
          <SortableMenuItem
            key={mealItem.id}
            mealItem={mealItem}
            target={target}
            onRemoveMealItem={onRemoveMealItem}
          />
        ))
      )}
      <TableRow>
        <AddTextItemCell
          options={mealItemSelectionList}
          onAddItem={onAddItem}
        />
      </TableRow>
    </TableBody>
  );
};
